import type { Result } from '@api';
import { Success, Failure, useErrorMessage, useDatahubAPI } from '@api';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import type { ScanMatchinScoreType } from '@data/datahub/scanMatchingScore/types';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import type { AxiosError, AxiosResponse } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useScanMatchingScoreTypeAPI = (): {
  isLoading: boolean;
  getScanMatchingScoreType: () => Promise<ScanMatchinScoreType[]>;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const { notifyError } = useNotification();
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const getHasScope = useHasScope();
  const { datahubAPI } = useDatahubAPI();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
    ): Promise<Result<ScanMatchinScoreType[], AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/scan_matching_score_types`,
        );
        return new Success(res?.data.score_types);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getScanMatchingScoreType = useAtomCallback(
    useCallback(
      async (get) => {
        if (!getHasScope(SCOPES.DescribeScanMatchingScore)) {
          // 権限が無い場合は処理を中断
          return [];
        }

        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.data.get_scan_matching_score_type', {
              status: 'failed',
            }),
          });
          return [];
        }
        setIsLoading(true);
        const res = await request(project.id, environment.environment_id);
        setIsLoading(false);
        if (res.isFailure()) {
          if (res.value?.status !== 404) {
            notifyError({
              message: `${t('api.data.get_scan_matching_score_type', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
          }
          return [];
        }
        return res.value;
      },
      [notifyError, t, request, getErrorMessage, getHasScope],
    ),
  );

  return {
    isLoading,
    getScanMatchingScoreType,
  };
};
